<template>
  <view-section v-if="collection" :title="$t('compatibility')" padding-y>
    <base-grid :cols="2">
      <div
        v-for="({ percentage, votes, label }, index) in stats"
        :key="index"
        class="tw-relative tw-mx-auto"
      >
        <p class="tw-text-xs tw-text-center">{{ $t(label) }}</p>
        <arc
          :success="percentage >= 70"
          :warning="percentage >= 60"
          :danger="percentage < 60 && votes !== 0"
          :percent="percentage / 100"
        />
        <div
          class="tw-absolute tw-bottom-0 tw-inset-x-0 tw-text-xs tw-text-center"
        >
          <p
            class="tw-font-bold"
            :class="{
              'tw-text-success': percentage >= 70,
              'tw-text-warning': percentage >= 60 && percentage < 70,
              'tw-text-danger': percentage < 60 && votes !== 0,
              'tw-text-primary': votes === 0,
            }"
          >
            {{ percentage }}%
          </p>
          <p>
            {{ numberFormatter(votes) }}
            {{ $t('vote', votes) }}
          </p>
        </div>
      </div>
    </base-grid>
    <base-divider />
    <div v-if="!isCurator && Settings.allowRating" class="tw-mt-2">
      <p class="tw-text-p-sm tw-mb-2">{{ $t('does_collection_work') }}</p>
      <base-grid :cols="2" :x-gap="3">
        <base-button
          class="tw-mx-auto"
          auto-width
          :success="vote === RATINGS_LIKE"
          :secondary="vote !== RATINGS_LIKE"
          @click="
            updateVote(vote === RATINGS_LIKE ? RATINGS_UNDECIDED : RATINGS_LIKE)
          "
        >
          {{ $t('collection_works') }}
        </base-button>
        <base-button
          class="tw-mx-auto"
          auto-width
          :danger="vote === RATINGS_DISLIKE"
          :secondary="vote !== RATINGS_DISLIKE"
          @click="
            updateVote(
              vote === RATINGS_DISLIKE ? RATINGS_UNDECIDED : RATINGS_DISLIKE
            )
          "
        >
          {{ $t('collection_doesnt_work') }}
        </base-button>
      </base-grid>
    </div>
  </view-section>
</template>

<script>
import { updateCollectionCompatibilityRequest } from '@services'
import ViewSection from '@components/View/ViewSection.vue'
import { setGlobalModal } from '@composables/useModal.js'
import { getRoundedPercentage } from '@helpers/utils.js'
import { MODAL_LOGIN } from '@config/globalModals.js'
import { numberFormatter } from '@helpers/utils.js'
import { ratingStore, authStore } from '@stores'
import { Settings } from '@config/game.js'
import Arc from '@components/Misc/Arc.vue'
import { useAsync } from '@composables'
import { computed, toRefs } from 'vue'
import {
  RESOURCE_COLLECTIONS,
  RATINGS_UNDECIDED,
  RATINGS_DISLIKE,
  RATINGS_LIKE,
} from '@config/options.js'
import {
  RATING_RESOURCE_ALREADY_RATED,
  RATING_RESOURCE_NOT_RATED,
} from '@shared/js/config/errors.js'

export default {
  components: {
    ViewSection,
    Arc,
  },
  props: {
    game: {
      type: Object,
      required: true,
    },
    collection: {
      type: Object,
      default: null,
    },
  },
  setup(props) {
    const { collection, game } = toRefs(props)

    const {
      updateUserCollectionRating,
      updateCollectionRating,
      ratings: userRatings,
    } = ratingStore()

    const { isLoggedIn, user } = authStore()

    const thirtyDaysVotes = computed(
      () => collection.value?.stats?.ratings_total_30days
    )

    const allTimeVotes = computed(() => collection.value?.stats?.ratings_total)

    const thirtyDaysPercentage = computed(() =>
      getRoundedPercentage(
        collection.value?.stats?.ratings_positive_30days,
        collection.value?.stats?.ratings_total_30days
      )
    )

    const allTimePercentage = computed(() =>
      getRoundedPercentage(
        collection.value?.stats?.ratings_positive,
        collection.value?.stats?.ratings_total
      )
    )

    const stats = computed(() => [
      {
        percentage: thirtyDaysPercentage.value,
        votes: thirtyDaysVotes.value,
        label: 'last_30_days',
      },
      {
        percentage: allTimePercentage.value,
        votes: allTimeVotes.value,
        label: 'all_time',
      },
    ])

    const vote = computed(() => {
      if (userRatings.value && collection.value && game.value) {
        const vote = userRatings.value.find(
          (vote) =>
            vote.resource_id === collection.value.id &&
            vote.resource_type === RESOURCE_COLLECTIONS &&
            vote.game_id === game.value.id
        )

        if (vote) return vote.rating
      }

      return RATINGS_UNDECIDED
    })

    const isCurator = computed(
      () => collection.value.submitted_by?.id === user.value?.id
    )

    const {
      errorRef: collectionRatingErrorRef,
      run: runUpdateCollectionRating,
      error: collectionRatingError,
    } = useAsync((rating) =>
      updateCollectionCompatibilityRequest({
        collectionNameId: collection.value.name_id,
        rating,
      })
    )

    async function updateVote(update) {
      if (!isLoggedIn.value) {
        setGlobalModal({ modal: MODAL_LOGIN })
        return
      }

      if (vote.value === RATINGS_UNDECIDED) {
        _updateVote(update)
      } else if (vote.value === RATINGS_LIKE && update === RATINGS_DISLIKE) {
        _updateVote(RATINGS_DISLIKE)
      } else if (vote.value === RATINGS_DISLIKE && update === RATINGS_LIKE) {
        _updateVote(RATINGS_LIKE)
      } else {
        _updateVote(RATINGS_UNDECIDED)
      }
    }

    async function _updateVote(update) {
      const currentRating = vote.value
      updateCollectionRating({
        gameNameId: game.value.name_id,
        collectionNameId: collection.value.name_id,
        update,
        current: currentRating,
      })
      updateUserCollectionRating(game.value.id, collection.value.id, update)

      await runUpdateCollectionRating(update)

      if (collectionRatingError.value) {
        // if errors revert rating change
        updateCollectionRating({
          gameNameId: game.value.name_id,
          collectionNameId: collection.value.name_id,
          update: currentRating,
          current: update,
        })
        if (
          ![RATING_RESOURCE_ALREADY_RATED, RATING_RESOURCE_NOT_RATED].includes(
            collectionRatingErrorRef.value
          )
        ) {
          updateUserCollectionRating(
            game.value.id,
            collection.value.id,
            currentRating
          )
        }
      }
    }

    return {
      RATINGS_UNDECIDED,
      RATINGS_DISLIKE,
      numberFormatter,
      RATINGS_LIKE,
      updateVote,
      isLoggedIn,
      isCurator,
      Settings,
      stats,
      vote,
    }
  },
}
</script>
